import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Section } from "./fields";
import { DocumentUpload } from "./DocumentUpload";
import { TrustStrip } from "./OnboardingRail";
import { OwlLoader } from "@/components/OwlLoader";
import {
  ScanFace, Loader2, CheckCircle2, Camera, ShieldCheck, Clock,
} from "lucide-react";

const GUIDELINES = ["Clear, well-lit photo", "Eyes visible", "Face inside the oval",
  "Looking straight ahead", "No glasses or hat", "Live selfie — no screens"];

export const VerifyStep = ({ kyc, startKyc, docType, setDocType, docMeta, setDocMeta, onUploaded, beforeUpload }) => {
  const [mode, setMode] = useState("selfie");

  if (kyc === "done") {
    return (
      <Section title="Identity verified"
        subtitle="Thank you — that's the last step. Tap Finish to open your account.">
        <div className="rounded-2xl border border-[color:var(--tharwy-lime-700)]/50 bg-[color:var(--tharwy-lime)]/10 p-8 text-center" data-testid="kyc-success">
          <div className="mx-auto h-16 w-16 rounded-full bg-[color:var(--tharwy-ink-950)] flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-[color:var(--tharwy-lime)]" />
          </div>
          <div className="mt-5 font-heading text-xl font-bold">You're verified</div>
          <p className="text-sm text-muted-foreground mt-2 max-w-sm mx-auto leading-relaxed">
            Your details are encrypted and stored under our regulated data policy. You can review or update
            them anytime from your profile.
          </p>
        </div>
        <div className="mt-6"><TrustStrip /></div>
      </Section>
    );
  }

  return (
    <Section title="Verify your identity"
      subtitle="A one-time check that nobody else can open an account in your name. It takes about a minute.">
      <div className="flex gap-2 mb-7 p-1 rounded-full bg-secondary w-fit" data-testid="kyc-mode-switch">
        {[["selfie", "Selfie + document"], ["upload", "Upload document"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setMode(id)} data-testid={`kyc-mode-${id}`}
            className={`rounded-full px-4 min-h-10 text-sm font-semibold transition-colors ${
              mode === id ? "bg-card text-foreground shadow-premium" : "text-muted-foreground hover:text-foreground"}`}>
            {label}
          </button>
        ))}
      </div>

      {mode === "selfie" ? (
        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-8 flex flex-col items-center text-center">
            <div className={`h-20 w-20 rounded-2xl flex items-center justify-center transition-colors ${
              kyc === "scanning" ? "bg-[color:var(--tharwy-ink-950)]" : "bg-secondary"}`}>
              {kyc === "scanning"
                ? <OwlLoader size="lg" />
                : <ScanFace className="h-9 w-9 text-[color:var(--tharwy-ink-950)] dark:text-[color:var(--tharwy-lime)]" />}
            </div>
            <div className="mt-5 font-semibold">{kyc === "scanning" ? "Checking — hold still…" : "Live selfie & document scan"}</div>
            <p className="text-sm text-muted-foreground mt-2 max-w-sm leading-relaxed">
              Handled by our regulated verification partner. Your images are transmitted over an encrypted
              channel and are never shared for marketing.
            </p>
            <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> Most checks clear in under a minute
            </p>
          </div>

          <div className="rounded-2xl bg-secondary/60 p-5">
            <div className="text-sm font-semibold mb-3">Before you start</div>
            <div className="grid sm:grid-cols-2 gap-y-2.5 gap-x-4 text-xs text-muted-foreground">
              {GUIDELINES.map((g) => (
                <span key={g} className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />{g}
                </span>
              ))}
            </div>
          </div>

          <Button onClick={startKyc} disabled={kyc === "scanning"} className="w-full h-12 rounded-full gap-2"
            data-testid="kyc-start">
            {kyc === "scanning" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}
            {kyc === "scanning" ? "Verifying…" : "Start secure verification"}
          </Button>
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <ShieldCheck className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)]" />
            No camera? Switch to “Upload document” above.
          </p>
        </div>
      ) : (
        <DocumentUpload docType={docType} setDocType={setDocType} meta={docMeta} setMeta={setDocMeta}
          onUploaded={onUploaded} beforeUpload={beforeUpload} />
      )}

      <div className="mt-8"><TrustStrip /></div>
    </Section>
  );
};
